import { ApiConsentTypeFabricator } from '@app/tests/utils/fabricators/api/ApiConsentType'
import { ApiCurrencyFabricator } from '@app/tests/utils/fabricators/api/ApiCurrency'
import { ApiCurrencyRateFabricator } from '@app/tests/utils/fabricators/api/ApiCurrencyRate'
import { ApiPlaceFabricator } from '@app/tests/utils/fabricators/api/ApiPlace'
import { ApiPolicyFabricator } from '@app/tests/utils/fabricators/api/ApiPolicy'
import { ApiRegionFabricator } from '@app/tests/utils/fabricators/api/ApiRegion'
import { ApiTimeZoneFabricator } from '@app/tests/utils/fabricators/api/ApiTimezone'

import type { ApiActionCallback } from '@app/packages/api/utils'
import { readApiRequest } from '@app/packages/api/utils'
import type { V3GetAuthYandexConfigResponse } from '@app/packages/api/v3/types/responses'

/**
 * What the polygon asks of the app's interceptor, stated here rather than imported: the package
 * renders app data, it does not reach into app services.
 */
type Interceptor = {
  intercept: (interceptor: (input: { label: string; action: ApiActionCallback<any> }) => (() => Promise<unknown>) | undefined | 'pass') => () => void
  /** The stub is handed the action, so it can answer about what was asked — see `readApiRequest`. */
  interceptApi: <R>(label: string, stub: (action: ApiActionCallback<any>) => Promise<R>) => () => void
}

export class ApiInterceptor {
  private interceptor: Interceptor
  private subscriptions: (() => unknown)[] = []

  constructor(interceptor: Interceptor) {
    this.interceptor = interceptor
  }

  interceptInitial() {
    this.subscriptions.push(
      this.interceptor.intercept(({ label }) => {
        return async () => {
          throw new Error(`Interceptor for ${label} not found`)
        }
      })
    )

    this.subscriptions.push(this.interceptor.interceptApi('getRegions', async () => ({ data: [new ApiRegionFabricator().get()], included: [] })))

    this.subscriptions.push(this.interceptor.interceptApi('getCurrencies', async () => ({ data: [new ApiCurrencyFabricator().get()] })))

    this.subscriptions.push(this.interceptor.interceptApi('getCurrencyRates', async () => ({ data: [new ApiCurrencyRateFabricator().get()] })))

    // Any polygon holding a `PlaceInput` resolves the place id in the reader's cookie, so this
    // fires or not depending on whether that browser has one — which is why it looks intermittent.
    //
    // The id has to be the one asked for: the caller fetches by id and then reads the place back
    // out of the store under that same id, so a fabricated id resolves to «Place not found after
    // fetch» and the stub looks like it never ran.
    this.subscriptions.push(
      this.interceptor.interceptApi('getPlacesById', async action => {
        const region = new ApiRegionFabricator().get()
        const id = readApiRequest(action)?.endpoint.match(/\/places\/([^/?]+)/)?.[1]
        const place = new ApiPlaceFabricator().bindRegion(region)
        return { data: (id ? place.setId(decodeURIComponent(id)) : place).get(), included: [region] }
      })
    )

    // Address entry and the address picker: whatever is typed or pointed at is the same house.
    const place = {
      id: 'place',
      type: 'place_suggestions',
      attributes: {
        title: 'г Москва, ул Тверская, д 1',
        subtitle: null,
        address: null,
        latitude: 55.757,
        longitude: 37.615,
        kind: 'house',
        precision: 'exact',
        locality: 'Москва',
      },
    }
    this.subscriptions.push(this.interceptor.interceptApi('getPlacesSuggest', async () => ({ data: [place] })))
    this.subscriptions.push(this.interceptor.interceptApi('getPlacesReverse', async () => ({ data: [place] })))

    // An address picked in a form asks for its timezone, and the announcement form asks again on mount
    // for the one it restored. The name goes to `moment.tz`, so it is the zone id, as the API sends it.
    this.subscriptions.push(
      this.interceptor.interceptApi('getTimezonesByLocation', async () => ({
        data: new ApiTimeZoneFabricator().setId('Europe/Moscow').setAttr('name', 'Europe/Moscow').setAttr('utc_offset', 10800).get(),
      }))
    )

    // Every login screen offers «Яндекс ID», and asks for its client id before it renders.
    this.subscriptions.push(
      this.interceptor.interceptApi('getAuthYandexConfig', async (): Promise<V3GetAuthYandexConfigResponse> => ({
        data: { id: 'polygon', type: 'yandex_id_configs', attributes: { client_id: 'polygon' } },
      }))
    )

    // Reached from the footer and the cookie banner, so most polygons pull it in without asking.
    this.subscriptions.push(
      this.interceptor.interceptApi('getPolicies', async () => ({
        data: [new ApiPolicyFabricator().get()],
        included: [new ApiConsentTypeFabricator().get()],
      }))
    )
  }
}
